import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, CalendarDays, MapPin, Search, Star } from 'lucide-react';
import { fetchAllVenues, fetchAllCities, City, Venue } from '../services/api';
import SearchBar from './SearchBar';
import { ComingSoonBadge, BARS_COMING_SOON } from './ComingSoon';
import '../styles/discovery.css';
import { fetchRestaurantMap, MapRestaurant } from '../services/restaurantMap';

const HomePage = () => {
  const [cities, setCities] = useState<City[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [currentRestaurants, setCurrentRestaurants] = useState<MapRestaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // Search and sort live in the URL so a filtered list can be linked or
  // reloaded, and the back button steps through it.
  const [params, setParams] = useSearchParams();
  const query = params.get('q') || '';
  const sort = params.get('sort') || 'venues';
  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setParams(next, { replace: true });
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    Promise.all([fetchAllCities(), fetchAllVenues(), fetchRestaurantMap()])
      .then(([allCities, allVenues, restaurants]) => {
        if (!active) return;
        setVenues(allVenues);
        setCurrentRestaurants(restaurants);
        setCities(
          allCities.filter(city =>
            allVenues.some(venue => venue.city.toLowerCase() === city.name.toLowerCase())
          )
        );
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [attempt]);

  // Restaurant eligibility comes from the same current-starred, open-venue
  // endpoint as the map. Bar ratings retain their separate latest guide year.
  const latestYear = useMemo(
    () =>
      venues.filter(venue => venue.venue_type === 'BAR').reduce(
        (max, venue) =>
          venue.yearly_ratings.reduce((m, rating) => Math.max(m, rating.year), max),
        0
      ),
    [venues]
  );

  const destinations = useMemo(() => {
    const currentRestaurantIds = new Set(currentRestaurants.map(venue => venue.id));
    const isCurrentlyRated = (venue: Venue) =>
      venue.venue_type === 'RESTAURANT' ? currentRestaurantIds.has(venue.id) :
        venue.yearly_ratings.some(rating => rating.year === latestYear);

    return cities.map(city => {
      const cityVenues = venues.filter(
        venue => venue.city.toLowerCase() === city.name.toLowerCase() && isCurrentlyRated(venue)
      );
      return {
        ...city,
        cityVenues,
        restaurantCount: currentRestaurants.filter(v => v.city?.toLowerCase() === city.name.toLowerCase()).length,
        barCount: cityVenues.filter(v => v.venue_type === 'BAR').length,
      };
    });
  }, [cities, venues, latestYear, currentRestaurants]);

  const ratedVenueCount = useMemo(
    () => destinations.reduce((total, city) => total + (BARS_COMING_SOON ? city.restaurantCount : city.cityVenues.length), 0),
    [destinations]
  );

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return destinations
      .filter(city =>
        !needle ||
        `${city.name} ${city.cityVenues.map(v => `${v.name} ${v.country}`).join(' ')}`
          .toLowerCase()
          .includes(needle)
      )
      .sort((a, b) =>
        sort === 'name'
          ? a.name.localeCompare(b.name)
          : b.restaurantCount - a.restaurantCount || a.name.localeCompare(b.name)
      );
  }, [destinations, query, sort]);

  return (
    <div className="discovery">
      <section className="discovery-hero">
        <div className="hero-copy">
          <span className="eyebrow">
            <Star size={14} /> A life well tasted
          </span>
          <h1>
            Your next
            <br />
            <em>unforgettable table.</em>
          </h1>
          <p>
            Discover exceptional restaurants and bars, keep a record of where you&rsquo;ve been,
            and find your next reservation.
          </p>
          <div className="hero-actions">
            <a href="#destinations" className="primary-action">
              Explore destinations <ArrowRight size={17} />
            </a>
            <Link to="/availability" className="secondary-action">
              <CalendarDays size={17} /> Find a table
            </Link>
          </div>
          {!loading && !error && (
            <div className="discovery-stats">
              <span>
                <strong>{destinations.length}</strong>
                {destinations.length === 1 ? 'city' : 'cities'} to explore
              </span>
              <span>
                <strong>{ratedVenueCount.toLocaleString()}</strong>
                {BARS_COMING_SOON ? 'Michelin-starred restaurants' : 'exceptional venues'}
              </span>
            </div>
          )}
        </div>
        <aside className="hero-note">
          <span className="note-star" aria-hidden="true">
            ✳
          </span>
          <span className="eyebrow">The joy of discovery</span>
          <h2>
            Great meals.
            <br />
            Lasting memories.
          </h2>
          <p>
            From Michelin stars to remarkable bars. Build your own story, one visit at a time.
          </p>
          <Link to="/dashboard">
            View your dining journey <ArrowRight size={17} />
          </Link>
        </aside>
      </section>

      <section id="destinations" className="destinations" aria-labelledby="destinations-title">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Somewhere worth going</span>
            <h2 id="destinations-title">Choose your next city</h2>
          </div>
          <span className="section-caption">A new favourite is waiting.</span>
        </div>

        <div className="discovery-toolbar">
          <SearchBar
            value={query}
            onChange={value => update('q', value)}
            placeholder="Search a city, country or restaurant"
            className="destination-search"
          />
          <label className="sort-control">
            Sort by
            <select value={sort} onChange={e => update('sort', e.target.value)}>
              <option value="venues">Most venues</option>
              <option value="name">City A&ndash;Z</option>
            </select>
          </label>
        </div>

        {loading ? (
          <div className="destination-grid is-sparse" role="status" aria-label="Loading destinations">
            {[1, 2].map(i => (
              <div key={i} className="destination-skeleton" />
            ))}
          </div>
        ) : error ? (
          <div className="discovery-empty" role="alert">
            <h3>We couldn&rsquo;t load your destinations.</h3>
            <p>Please check your connection and try again.</p>
            <button type="button" className="primary-action" onClick={() => setAttempt(a => a + 1)}>
              Try again
            </button>
          </div>
        ) : (
          <>
            <p className="results-caption" role="status">
              {visible.length} {visible.length === 1 ? 'destination' : 'destinations'}
              {query && ` matching “${query}”`}
            </p>

            {visible.length === 0 ? (
              <div className="discovery-empty">
                <Search size={28} />
                <h3>{query ? 'No destinations found' : 'Your next chapter is on its way'}</h3>
                <p>
                  {query
                    ? 'Try another city, country or restaurant name.'
                    : 'Destinations will appear when venue data is available.'}
                </p>
                {query && (
                  <button type="button" className="secondary-action" onClick={() => update('q', '')}>
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              <div className={`destination-grid${visible.length <= 2 ? ' is-sparse' : ''}`}>
                {visible.map((city, index) => {
                  // A city with no currently-rated restaurants has nothing to
                  // show, so present it as upcoming rather than as an empty
                  // list. Derived from the data rather than naming cities, so
                  // it clears itself the moment that city's guide is imported.
                  const isComingSoon = city.restaurantCount === 0;

                  const card = (
                    <>
                      <div className="destination-photo">
                        <img
                          src={city.proxied_image || city.image_url || '/default_lazy_restaurant.png'}
                          alt=""
                          loading={index < 3 ? 'eager' : 'lazy'}
                          onError={e => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '/default_lazy_restaurant.png';
                          }}
                        />
                        {isComingSoon && (
                          <span className="city-badge">
                            <ComingSoonBadge />
                          </span>
                        )}
                        <div className="city-title">
                          <MapPin size={16} />
                          <h3>{city.name}</h3>
                        </div>
                      </div>

                      <div className="destination-meta">
                        {isComingSoon ? (
                          // Mirrors the live card's stat row so the two line
                          // up, with the numbers held back rather than
                          // replaced by a status claim we cannot stand behind.
                          <span className="meta-muted">
                            &mdash; restaurants <span aria-hidden="true">·</span> &mdash; bars
                          </span>
                        ) : (
                          <span>
                            {city.restaurantCount} Michelin-starred restaurant{city.restaurantCount === 1 ? '' : 's'}{' '}
                            <span aria-hidden="true">·</span>{' '}
                            {/* Bars are real, rated venues in the data - the guide
                                just isn't published yet (VenuePage's Bars tab is a
                                placeholder). A live count here would promise a
                                section that isn't there when you click through. */}
                            {BARS_COMING_SOON ? (
                              <span className="meta-muted">&mdash; bars</span>
                            ) : (
                              <>
                                {city.barCount} bar{city.barCount === 1 ? '' : 's'}
                              </>
                            )}
                          </span>
                        )}
                        {!isComingSoon && <ArrowRight size={18} />}
                      </div>
                    </>
                  );

                  // Not a link while empty - following it would only show
                  // "0 found".
                  return isComingSoon ? (
                    <div
                      key={city.id}
                      aria-disabled="true"
                      className="destination-card is-upcoming"
                    >
                      {card}
                    </div>
                  ) : (
                    <Link
                      key={city.id}
                      to={`/venues/${city.name}`}
                      className="destination-card"
                    >
                      {card}
                    </Link>
                  );
                })}
              </div>
            )}
          </>
        )}
      </section>

      <section className="discovery-banner">
        <div>
          <span className="eyebrow">Make an evening of it</span>
          <h2>Found your city? Find your table.</h2>
          <p>
            Browse collected reservation slots. Availability is confirmed by the booking provider.
          </p>
        </div>
        <Link className="primary-action" to="/availability">
          Check availability <ArrowRight size={17} />
        </Link>
      </section>
    </div>
  );
};

export default HomePage;
